import { ApiError } from '@/api/http';

type ValidationDetail = { loc?: unknown[]; msg?: string; type?: string };
type ApiErrorDetail = { message?: string; code?: string; subscriptionId?: string };

/** Human-readable message for any thrown/rejected API or UI error value. */
export function formatApiError(err: unknown, fallback = 'Request failed'): string {
  if (typeof err === 'string' && err.trim()) {
    return err;
  }
  if (err instanceof ApiError) {
    const detail = err.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail)) {
      const parts = (detail as ValidationDetail[])
        .map((item) => {
          const field = Array.isArray(item.loc)
            ? item.loc.filter((part) => part !== 'body' && part !== 'path').join('.')
            : '';
          const msg = item.msg || item.type || 'invalid';
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (parts.length) return parts.join('; ');
    }
    if (detail && typeof detail === 'object') {
      const obj = detail as ApiErrorDetail;
      if (typeof obj.message === 'string' && obj.message.trim()) {
        return obj.message;
      }
    }
    if (err.message && err.message !== '[object Object]') {
      return err.message;
    }
  }
  if (err instanceof Error && err.message && err.message !== '[object Object]') {
    return err.message;
  }
  if (err && typeof err === 'object') {
    const obj = err as { message?: unknown; detail?: unknown };
    if (typeof obj.message === 'string' && obj.message.trim()) {
      return obj.message;
    }
    if (typeof obj.detail === 'string' && obj.detail.trim()) {
      return obj.detail;
    }
  }
  return fallback;
}

export function getErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  return formatApiError(err, fallback);
}

function readApiErrorDetail(err: unknown): ApiErrorDetail | null {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object') {
    return err.detail as ApiErrorDetail;
  }
  if (err && typeof err === 'object') {
    const nested = (err as { detail?: unknown }).detail;
    if (nested && typeof nested === 'object') {
      return nested as ApiErrorDetail;
    }
  }
  return null;
}

export function isDuplicateSubscriptionError(err: unknown): boolean {
  return readApiErrorDetail(err)?.code === 'duplicate_subscription';
}

export function duplicateSubscriptionId(err: unknown): string | null {
  const id = readApiErrorDetail(err)?.subscriptionId;
  return typeof id === 'string' && id.trim() ? id : null;
}

export function duplicateSubscriptionMessage(err: unknown): string {
  return formatApiError(
    err,
    'This recipient is already subscribed to this search.',
  );
}
